import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'

export function AccountPage() {
  const { user, profile, refreshProfile, signOut } = useAuth()
  const navigate = useNavigate()
  const [displayName, setDisplayName] = useState('')
  const [surname, setSurname] = useState('')
  const [affiliation, setAffiliation] = useState('')
  const [orcid, setOrcid] = useState('')
  const [scholarUrl, setScholarUrl] = useState('')
  const [researchInterests, setResearchInterests] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [deleteText, setDeleteText] = useState('')
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    setDisplayName(profile?.display_name ?? '')
    setSurname(profile?.surname ?? '')
    setAffiliation(profile?.affiliation ?? '')
    setOrcid(profile?.orcid ?? '')
    setScholarUrl(profile?.scholar_url ?? '')
    setResearchInterests(profile?.research_interests ?? '')
  }, [profile])

  const save = async (event: FormEvent) => {
    event.preventDefault()
    if (!user) return
    setSaving(true)
    setError(null)
    setMessage(null)

    const { error: updateError } = await supabase
      .from('profiles')
      .update({
        display_name: displayName.trim() || null,
        surname: surname.trim() || null,
        affiliation: affiliation.trim() || null,
        orcid: orcid.trim() || null,
        scholar_url: scholarUrl.trim() || null,
        research_interests: researchInterests.trim() || null,
      })
      .eq('id', user.id)

    if (updateError) setError(updateError.message)
    else {
      await refreshProfile()
      setMessage('Optional academic profile updated.')
    }
    setSaving(false)
  }

  const deleteAccount = async () => {
    if (deleteText !== 'DELETE') return
    const confirmed = window.confirm(
      'Permanently close your account? Active submissions, uploaded PDFs, stored arXiv identifiers, and private feedback will be deleted.',
    )
    if (!confirmed) return

    setDeleting(true)
    setError(null)
    const { error: functionError } = await supabase.functions.invoke('delete-account')
    if (functionError) {
      setError(functionError.message)
      setDeleting(false)
      return
    }

    await signOut()
    navigate('/', { replace: true })
  }

  return (
    <section className="section">
      <div className="container narrow-container">

        <div className="panel">
          <form className="stack-form" onSubmit={save}>
            <label htmlFor="account-email">Email</label>
            <input id="account-email" value={user?.email ?? ''} disabled />

            <div className="form-grid-two">
              <div>
                <label htmlFor="display-name">First name</label>
                <input
                  id="display-name"
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                />
              </div>
              <div>
                <label htmlFor="surname">Last name</label>
                <input
                  id="surname"
                  value={surname}
                  onChange={(event) => setSurname(event.target.value)}
                />
              </div>
            </div>

            <label htmlFor="affiliation">Affiliation</label>
            <input
              id="affiliation"
              value={affiliation}
              onChange={(event) => setAffiliation(event.target.value)}
            />

            <div className="form-grid-two">
              <div>
                <label htmlFor="orcid">ORCID iD</label>
                <input
                  id="orcid"
                  value={orcid}
                  onChange={(event) => setOrcid(event.target.value)}
                  placeholder="0000-0000-0000-0000"
                />
              </div>
              <div>
                <label htmlFor="scholar-url">Google Scholar profile</label>
                <input
                  id="scholar-url"
                  type="url"
                  value={scholarUrl}
                  onChange={(event) => setScholarUrl(event.target.value)}
                  placeholder="https://scholar.google.com/..."
                />
              </div>
            </div>

            <label htmlFor="research-interests">Research interests</label>
            <textarea
              id="research-interests"
              rows={4}
              value={researchInterests}
              onChange={(event) => setResearchInterests(event.target.value)}
            />

            <button className="button button-primary" type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save optional information'}
            </button>
          </form>
          {message && <div className="notice notice-success">{message}</div>}
          {error && <div className="notice notice-error">{error}</div>}
        </div>

        <div className="panel danger-panel">
          <h2>Close account</h2>
          <p>
            Closing the account removes active submissions, uploaded PDFs, stored arXiv identifiers,
            and private feedback. Anonymous aggregate service statistics may remain.
          </p>
          <label htmlFor="delete-confirmation">
            Type <strong>DELETE</strong> to continue
          </label>
          <input
            id="delete-confirmation"
            value={deleteText}
            onChange={(event) => setDeleteText(event.target.value)}
          />
          <button
            className="button button-danger"
            type="button"
            disabled={deleteText !== 'DELETE' || deleting}
            onClick={() => void deleteAccount()}
          >
            {deleting ? 'Closing account...' : 'Permanently close account'}
          </button>
        </div>
      </div>
    </section>
  )
}
